import { Ban, CheckCircle2, Eye, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlarmReliabilityBadge, AlarmSeverityBadge } from "@/components/AlarmBadge";
import { DataTable } from "@/components/DataTable";
import {
  canJustify,
  isAlarmActive,
  occurrenceTitle,
  occurrenceType,
} from "@/features/attention/occurrence-meta";
import { OccurrenceStatusPills } from "@/features/attention/OccurrenceStatusPills";
import { formatRelativeTime } from "@/lib/display-labels";
import type { AttentionOccurrence } from "@/types/attention.types";

interface Props {
  occurrences: AttentionOccurrence[];
  scope: "pending" | "resolved";
  canResolve: boolean;
  onAck: (occ: AttentionOccurrence) => void;
  onResolve: (occ: AttentionOccurrence) => void;
  onJustify: (occ: AttentionOccurrence) => void;
  onMarkFalse: (occ: AttentionOccurrence) => void;
}

export function AlarmsTable({
  occurrences,
  scope,
  canResolve,
  onAck,
  onResolve,
  onJustify,
  onMarkFalse,
}: Props) {
  return (
    <DataTable
      data={occurrences}
      rowKey={(o) => o.occurrenceId}
      emptyState={
        scope === "pending"
          ? "Nenhuma pendência de atenção no momento. 🎉"
          : "Nenhum registro resolvido no histórico."
      }
      columns={[
        {
          header: "Linha",
          accessor: (o) => <span className="text-xs">{o.lineName || "—"}</span>,
        },
        {
          header: "Máquina",
          accessor: (o) => <span className="font-mono text-xs">{o.machineCode}</span>,
        },
        {
          header: "Título",
          accessor: (o) => (
            <div className="space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium">{occurrenceTitle(o)}</p>
                <AlarmSeverityBadge severity={o.severity} />
                {o.alarm ? <AlarmReliabilityBadge reliability={o.alarm.reliability} /> : null}
              </div>
              <p className="text-xs text-muted-foreground">{o.reason ?? o.action}</p>
            </div>
          ),
        },
        {
          header: "Tipo",
          accessor: (o) => <span className="text-xs">{occurrenceType(o)}</span>,
        },
        {
          header: "Status",
          accessor: (o) => (
            <div className="flex flex-wrap gap-1">
              <OccurrenceStatusPills occ={o} resolvedFallback />
            </div>
          ),
        },
        {
          header: scope === "resolved" ? "Resolvido" : "Início",
          accessor: (o) =>
            formatRelativeTime(scope === "resolved" ? (o.resolvedAt ?? o.since) : o.since),
          className: "text-muted-foreground text-xs",
        },
        {
          header: "",
          accessor: (o) => {
            if (scope === "resolved") return null;
            const canAck = o.alarm?.state === "UNACKNOWLEDGED";
            const canResolveAlarm = canResolve && isAlarmActive(o);
            // mark-false aceita UNACK + ACK (backend só rejeita RESOLVED/SHELVED) — = alarme ativo
            const canMarkFalse = canResolve && isAlarmActive(o);
            return (
              <div className="flex justify-end gap-1">
                {canAck ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Reconhecer ${occurrenceTitle(o)}`}
                    onClick={() => onAck(o)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                ) : null}
                {canResolveAlarm ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Resolver ${occurrenceTitle(o)}`}
                    onClick={() => onResolve(o)}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                ) : null}
                {canJustify(o) ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Justificar ${occurrenceTitle(o)}`}
                    onClick={() => onJustify(o)}
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                ) : null}
                {canMarkFalse ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Marcar falso ${occurrenceTitle(o)}`}
                    onClick={() => onMarkFalse(o)}
                  >
                    <Ban className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            );
          },
          className: "text-right",
        },
      ]}
    />
  );
}
